// New Game flow: "Articles of Incorporation" charter (company, founder, emblem, difficulty, seed) and the
// historical intro card that hands off to the existing onboarding.
// openCharter(host, { initial }) -> Promise<opts | null>; showIntro(host, opts, { setMood }) -> Promise<void>;
// createGame(api, opts) -> GameState (calls api.newGame per CONTRACT, then applies front-door extras).
import { esc, money } from './dom.js';
import { emblemSVG, randomLogo, normalizeLogo, MOTIFS, SHAPES, SCHEMES, initials } from './emblem.js';

// ---------- names ----------
const PLACES = ['Seneca', 'Venango', 'Allegheny', 'Oil Creek', 'Titusville', 'Franklin', 'Tidioute', 'Cherry Run', 'Clarion',
  'Warren', 'Kanawha', 'Crawford', 'Mercer', 'Butler', 'Pleasantville', 'Rouseville', 'French Creek', 'Pithole', 'Tionesta',
  'Monongahela', 'Conewango', 'Brokenstraw', 'Petroleum Centre', 'Sugar Creek', 'Shamburg', 'Cornplanter'];
const ADJ = ['Keystone', 'Union', 'Eagle', 'Empire', 'Columbia', 'Excelsior', 'Crescent', 'Mutual', 'Atlantic', 'Pioneer',
  'Phoenix', 'Liberty', 'Evening Star', 'Diamond', 'Iron City', 'Lamp & Wick', 'Rock City', 'Northern Light'];
const SURNAMES = ['Bissell', 'Eveleth', 'Watson', 'Brewer', 'Rouse', 'Funk', 'Tarr', 'Phillips', 'Abbott', 'Harley',
  'Vandergrift', 'Lockhart', 'Frew', 'Benninghoff', 'Hyde', 'Galey', 'Merritt', 'Ludlow', 'Crocker', 'Holmden', 'Steele',
  'Wilhelm', 'McClintock', 'Story', 'Clapp', 'Hasson', 'Blood', 'Farrel', 'Kingsley', 'Prather', 'Whitcomb', 'Tuttle'];
const FIRST = ['Edwin', 'George', 'Jonathan', 'Ezra', 'Ambrose', 'Silas', 'Cornelia', 'Harriet', 'Josiah', 'Abigail', 'Thaddeus',
  'Mercy', 'Elias', 'Lucinda', 'Horatio', 'Samuel', 'William', 'Martha', 'Amos', 'Clara', 'Ida', 'Augustus', 'Phoebe',
  'Obadiah', 'Delia', 'Erastus', 'Hannah', 'Lyman', 'Temperance', 'Cyrus'];
const PATTERNS = [
  (r) => `${r(PLACES)} Rock Oil Co.`,
  (r) => `${r(PLACES)} Petroleum Co.`,
  (r) => `${r(PLACES)} Oil & Mining Co.`,
  (r) => `${r(SURNAMES)} & ${r(SURNAMES)}`,
  (r) => `${r(SURNAMES)} Brothers Oil`,
  (r) => `${r(ADJ)} Oil Works`,
  (r) => `The ${r(ADJ)} Oil Company`,
  (r) => `${r(PLACES)} Valley Oil Co.`,
  (r) => `${r(SURNAMES)}, ${r(SURNAMES)} & Co.`,
  (r) => `${r(SURNAMES)} Oil Works`,
  (r) => `${r(ADJ)} Rock Oil Co.`,
];
const pickR = (rand) => (a) => a[Math.floor(rand() * a.length)];
export function randomCompanyName(rand = Math.random) {
  for (let i = 0; i < 10; i++) {
    const r = pickR(rand);
    const n = PATTERNS[Math.floor(rand() * PATTERNS.length)](r);
    const parts = n.split(/[ ,&]+/);
    if (new Set(parts).size === parts.length && n.length <= 34) return n; // no "Tarr & Tarr"
  }
  return 'Seneca Rock Oil Co.';
}
export const randomFounder = (rand = Math.random) => `${pickR(rand)(FIRST)} ${pickR(rand)(SURNAMES)}`;

// ---------- difficulty ----------
// Applied UI-side when the sim's newGame() doesn't handle `difficulty` itself (see CONTRACT changelog).
export const DIFFICULTIES = [
  { id: 'wildcatter', name: 'Wildcatter', tag: 'Easy', cash: 3500, blurb: 'A fat purse and room for a dry hole or two.' },
  { id: 'independent', name: 'Independent', tag: 'Normal', cash: 2000, blurb: 'Fair odds. The game as designed.' },
  { id: 'baron', name: 'Baron', tag: 'Hard', cash: 1400, blurb: 'Thin margins. One bad well and the bank calls.' },
];
export const difficultyById = (id) => DIFFICULTIES.find((d) => d.id === id) ?? DIFFICULTIES[1];
// Starting purse for a difficulty in a given start year (Quick Starts scale their base capital by the same ratio).
const QS_BASE = { 1901: 12000, 1946: 1.5e6, 1973: 25e6, 2000: 60e6, 2014: 200e6 }; // M4-C: + 2000 / 2014
export const startingPurse = (diffId, startYear) => { const d = difficultyById(diffId); return startYear >= 1901 ? Math.round((QS_BASE[startYear] ?? 12000) * d.cash / 2000) : d.cash; };

export function seedFromText(txt) {
  const t = String(txt ?? '').trim();
  if (!t) return undefined;
  if (/^\d+$/.test(t)) return Number(t) >>> 0;
  return [...t].reduce((a, c) => (Math.imul(a, 31) + c.charCodeAt(0)) >>> 0, 7);
}

// Build a game through the contract's newGame(), then attach the front-door fields if the sim didn't.
export function createGame(api, opts = {}) {
  const diff = difficultyById(opts.difficulty);
  const startYear = [1901, 1946, 1973, 2000, 2014].includes(opts.startYear) ? opts.startYear : 1859; // M3 ops: 1946 / 1973 Quick Starts; M4-C: 2000 / 2014
  const s = api.newGame({ seed: opts.seed, companyName: opts.companyName, founderName: opts.founderName, logo: opts.logo, difficulty: diff.id, startYear });
  if (startYear === 1859) s.firstWellLuck = true; // the first well always strikes oil by 80 ft (actions.firstWellLuck)
  (s.settings ??= {}).handsOn = true; // hands-on drilling: you drive every well on the brake (Menu can let crews drill unattended)
  // Auto-pause on events starts off (Menu → Settings turns it on): strikes, dry holes, gushers, worn bits, full storage,
  // injuries and historical news show a card or notice without stopping the clock. Blowouts, attacks, accidents,
  // inventions, era changes and decisions that need an answer still pause.
  s.settings.autoPause ??= {}; for (const k of ['strike', 'dry', 'gusher', 'bitWorn', 'storageFull', 'news', 'injury']) s.settings.autoPause[k] = false;
  s.company ??= {};
  if (opts.logo && !s.company.logo) s.company.logo = normalizeLogo(opts.logo);
  if (opts.founderName && !s.company.founder) s.company.founder = String(opts.founderName).slice(0, 40);
  s.settings ??= {};
  if (!s.settings.difficulty) {
    // Sim hasn't adopted difficulty yet: scale starting cash from the $2,000 baseline.
    const base = 2000;
    if (Math.round(s.company.cash ?? base) === base) s.company.cash = diff.cash;
    else if (startYear >= 1901) s.company.cash = Math.round((s.company.cash ?? 0) * diff.cash / base);   // Quick Start: same difficulty ratio
    s.settings.difficulty = diff.id;
  }
  return s;
}

// ---------- charter (form) ----------
const DICE = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><rect x="3.5" y="3.5" width="17" height="17" rx="3.5" fill="none" stroke="currentColor" stroke-width="1.8"/><g fill="currentColor"><circle cx="8.5" cy="8.5" r="1.5"/><circle cx="15.5" cy="15.5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="15.5" cy="8.5" r="1.5"/><circle cx="8.5" cy="15.5" r="1.5"/></g></svg>';

export function openCharter(host, { initial = {} } = {}) {
  return new Promise((resolve) => {
    const st = {
      companyName: initial.companyName ?? randomCompanyName(),
      founderName: initial.founderName ?? randomFounder(),
      logo: normalizeLogo(initial.logo ?? randomLogo()),
      difficulty: initial.difficulty ?? 'independent',
      startYear: initial.startYear ?? 1859,
      seed: initial.seed ?? '',
    };
    host.innerHTML = `
      <form class="fd-charter" novalidate aria-labelledby="fd-ch-title">
        <div class="fd-ch-form">
          <div class="fd-eyebrow" data-role="juris">Commonwealth of Pennsylvania · Venango County</div>
          <h2 id="fd-ch-title" class="fd-h2">Articles of Incorporation</h2>
          <p class="fd-lede">Every oil company starts with a name, a mark and a little nerve.</p>

          <div class="fd-field">
            <label for="fd-name">Company name</label>
            <div class="fd-inrow">
              <input id="fd-name" name="name" type="text" maxlength="40" autocomplete="off" spellcheck="false" value="${esc(st.companyName)}">
              <button type="button" class="fd-dice" data-roll="name" aria-label="Random company name" title="Random period name">${DICE}</button>
            </div>
          </div>
          <div class="fd-field">
            <label for="fd-founder">Founder</label>
            <div class="fd-inrow">
              <input id="fd-founder" name="founder" type="text" maxlength="40" autocomplete="off" spellcheck="false" value="${esc(st.founderName)}">
              <button type="button" class="fd-dice" data-roll="founder" aria-label="Random founder name" title="Random founder">${DICE}</button>
            </div>
          </div>

          <fieldset class="fd-field">
            <legend>Starting year</legend>
            <div class="fd-diffs fd-years">
              <label class="fd-diff"><input type="radio" name="startYear" value="1859">
                <span><b>Pioneer · 1859</b><em>Titusville, Pennsylvania</em><small>A spring pole, two hands and Drake's rumour. The whole story.</small></span></label>
              <label class="fd-diff"><input type="radio" name="startYear" value="1901">
                <span><b>Gusher · 1901</b><em>Quick start · Beaumont, Texas</em><small>An established Oil Creek outfit with a rotary rig, eight days before Spindletop blows.</small></span></label>
              <label class="fd-diff"><input type="radio" name="startYear" value="1946">
                <span><b>Postwar · 1946</b><em>Quick start · Midland, Texas</em><small>A Texas independent with two rotary rigs, cash for a Middle East concession or a first Gulf platform — not both.</small></span></label>
              <label class="fd-diff"><input type="radio" name="startYear" value="1973">
                <span><b>Offshore · 1973</b><em>Quick start · Houston, Texas</em><small>Permian waterfloods, a Gulf platform, a jackup and a North Sea toe-hold — on the day OPEC raises the price 70%.</small></span></label>
              <label class="fd-diff"><input type="radio" name="startYear" value="2000"><!-- M4-C -->
                <span><b>Shale &amp; Deepwater · 2000</b><em>Quick start · Houston, Texas</em><small>Tired Permian waterfloods, a Gulf shelf platform, two good land rigs and three sections of an odd gas shale near Fort Worth. A Barnett programme or a deepwater share — not both.</small></span></label>
              <label class="fd-diff"><input type="radio" name="startYear" value="2014"><!-- M4-C -->
                <span><b>Top of the Cycle · 2014</b><em>Quick start · Midland, Texas</em><small>Thirty-two horizontals in the Permian, Bakken and Eagle Ford, a frac crew booked, half hedged at $95 and a $250M RBL — five months before OPEC refuses to cut.</small></span></label>
            </div>
          </fieldset>
          <fieldset class="fd-field">
            <legend>Difficulty</legend>
            <div class="fd-diffs">${DIFFICULTIES.map((d) => `
              <label class="fd-diff"><input type="radio" name="difficulty" value="${d.id}">
                <span><b>${esc(d.name)}</b><em data-role="purse-${d.id}">${esc(d.tag)} · ${esc(money(d.cash))}</em><small>${esc(d.blurb)}</small></span></label>`).join('')}</div>
          </fieldset>

          <fieldset class="fd-field fd-emblem-picker">
            <legend>Company emblem <button type="button" class="fd-linkbtn" data-roll="logo">${DICE} Shuffle</button></legend>
            <div class="fd-pick-lbl">Mark</div>
            <div class="fd-pick-row" role="radiogroup" aria-label="Motif">${MOTIFS.map((m) => tile('motif', m, m)).join('')}</div>
            <div class="fd-pick-lbl">Frame</div>
            <div class="fd-pick-row" role="radiogroup" aria-label="Shape">${SHAPES.map((sh) => tile('shape', sh, sh)).join('')}</div>
            <div class="fd-pick-lbl">Colours</div>
            <div class="fd-pick-row fd-swatches" role="radiogroup" aria-label="Colours">${SCHEMES.map((c, i) => `
              <label class="fd-swatch" title="${esc(c[0])}"><input type="radio" name="scheme" value="${i}"><span style="--a:${c[1]};--b:${c[2]};--c:${c[3]}"></span><i class="sr">${esc(c[0])}</i></label>`).join('')}</div>
          </fieldset>


          <details class="fd-field fd-adv">
            <summary>Advanced</summary>
            <label for="fd-seed">World seed <span class="fd-muted">(optional — same seed, same oil fields)</span></label>
            <input id="fd-seed" name="seed" type="text" maxlength="24" placeholder="random" autocomplete="off" value="${esc(st.seed)}">
          </details>
        </div>

        <aside class="fd-ch-preview" aria-live="polite">
          <div class="fd-cert">
            <div class="fd-cert-in">
              <div class="fd-cert-top">Capital Stock Certificate · No. 1</div>
              <div class="fd-cert-emblem" data-role="emblem"></div>
              <div class="fd-cert-name" data-role="cname"></div>
              <div class="fd-cert-line" data-role="incorp">Incorporated 1 August 1859 at Titusville, Pa.</div>
              <div class="fd-cert-cap"><span>Capital</span><b data-role="cap"></b></div>
              <div class="fd-cert-sign"><span data-role="fname"></span><small>Founder &amp; President</small></div>
              <div class="fd-stamp" data-role="stamp"></div>
            </div>
          </div>
        </aside>

        <div class="fd-ch-foot">
          <button type="button" class="fd-btn ghost" data-act="back">← Back</button>
          <button type="submit" class="fd-btn primary">Incorporate &amp; spud in →</button>
        </div>
      </form>`;

    function tile(group, val, label) {
      const desc = { ...st.logo, [group]: val };
      return `<label class="fd-tile" title="${esc(label)}"><input type="radio" name="${group}" value="${val}"><span data-tile="${group}:${val}">${emblemSVG(desc, { size: 40, title: label })}</span><i class="sr">${esc(label)}</i></label>`;
    }
    const form = host.querySelector('form');
    const $ = (sel) => form.querySelector(sel);
    const syncRadios = () => {
      for (const g of ['motif', 'shape']) form.querySelector(`input[name=${g}][value="${st.logo[g]}"]`).checked = true;
      form.querySelector(`input[name=scheme][value="${st.logo.scheme}"]`).checked = true;
      form.querySelector(`input[name=difficulty][value="${st.difficulty}"]`).checked = true;
      form.querySelector(`input[name=startYear][value="${st.startYear}"]`).checked = true;
    };
    const refreshTiles = () => {
      for (const el of form.querySelectorAll('[data-tile]')) {
        const [g, v] = el.dataset.tile.split(':');
        el.innerHTML = emblemSVG({ ...st.logo, [g]: v }, { size: 40, title: v });
      }
    };
    const preview = () => {
      const name = st.companyName.trim() || 'Unnamed Oil Co.';
      const place = START_PLACE[st.startYear] ?? START_PLACE[1859]; // charter text follows the starting year
      $('[data-role=emblem]').innerHTML = emblemSVG(st.logo, { size: 132, title: `${name} emblem`, ring: `${initials(name)} · EST. ${st.startYear} · ${place.ring} ·` });
      $('[data-role=juris]').textContent = place.juris;
      $('[data-role=incorp]').textContent = `Incorporated ${place.date} at ${place.town}`;
      $('[data-role=cname]').textContent = name;
      $('[data-role=fname]').textContent = st.founderName.trim() || '—';
      const d = difficultyById(st.difficulty);
      $('[data-role=cap]').textContent = money(startingPurse(d.id, st.startYear));
      for (const x of DIFFICULTIES) { const el = $(`[data-role=purse-${x.id}]`); if (el) el.textContent = `${x.tag} · ${money(startingPurse(x.id, st.startYear))}`; }
      $('[data-role=stamp]').textContent = d.name;
      $('[data-role=stamp]').dataset.diff = d.id;
    };
    syncRadios(); preview();

    form.addEventListener('input', (e) => {
      const t = e.target;
      if (t.name === 'name') st.companyName = t.value;
      else if (t.name === 'founder') st.founderName = t.value;
      else if (t.name === 'seed') st.seed = t.value;
      else if (t.name === 'motif' || t.name === 'shape') { st.logo[t.name] = t.value; refreshTiles(); }
      else if (t.name === 'scheme') { st.logo.scheme = +t.value; refreshTiles(); }
      else if (t.name === 'difficulty') st.difficulty = t.value;
      else if (t.name === 'startYear') st.startYear = +t.value;
      preview();
    });
    form.addEventListener('click', (e) => {
      const b = e.target.closest('[data-roll],[data-act]');
      if (!b) return;
      if (b.dataset.roll === 'name') { st.companyName = randomCompanyName(); $('#fd-name').value = st.companyName; }
      if (b.dataset.roll === 'founder') { st.founderName = randomFounder(); $('#fd-founder').value = st.founderName; }
      if (b.dataset.roll === 'logo') { st.logo = randomLogo(); syncRadios(); refreshTiles(); }
      if (b.dataset.act === 'back') return finish(null);
      preview();
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      finish({
        companyName: (st.companyName.trim() || randomCompanyName()).slice(0, 40),
        founderName: (st.founderName.trim() || randomFounder()).slice(0, 40),
        logo: normalizeLogo(st.logo),
        difficulty: st.difficulty,
        startYear: st.startYear,
        seed: seedFromText(st.seed),
        seedText: st.seed.trim(),
      });
    });
    form.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); finish(null); } });
    function finish(v) { resolve(v); }
    requestAnimationFrame(() => { const n = $('#fd-name'); n.focus(); n.select(); });
  });
}

// Where and when the company is chartered, per starting year (certificate preview).
const START_PLACE = {
  1859: { juris: 'Commonwealth of Pennsylvania · Venango County', date: '1 August 1859', town: 'Titusville, Pa.', ring: 'TITUSVILLE PA' },
  1901: { juris: 'State of Texas · Jefferson County', date: '2 January 1901', town: 'Beaumont, Tex.', ring: 'BEAUMONT TEX' },
  1946: { juris: 'State of Texas · Midland County', date: '2 January 1946', town: 'Midland, Tex.', ring: 'MIDLAND TEX' },
  1973: { juris: 'State of Texas · Harris County', date: '16 October 1973', town: 'Houston, Tex.', ring: 'HOUSTON TEX' },
  2000: { juris: 'State of Texas · Harris County', date: '3 January 2000', town: 'Houston, Tex.', ring: 'HOUSTON TEX' }, // M4-C
  2014: { juris: 'State of Texas · Midland County', date: '20 June 2014', town: 'Midland, Tex.', ring: 'MIDLAND TEX' }, // M4-C
};

// ---------- intro card ----------
// M3 ops: prologues for the 1946 and 1973 Quick Starts
const INTRO_M3 = (opts, esc) => ({
  1946: [`<div class="fd-dateline">January 1946 · Midland, Texas</div>`, `<h2 class="fd-intro-head">The war is over. The world wants oil.</h2>`,
    `<p>Surplus diesels and landing craft are going cheap. Aramco is drilling Arabia, and a man named Kerr wants to put a rig out of sight of land in the Gulf of Mexico.</p>`,
    `<p><b>${esc(opts.founderName)}</b>'s <b>${esc(opts.companyName)}</b> has wells in East Texas, the Permian and Signal Hill, and two rotary rigs.</p>`,
    `<p class="fd-kicker">Go east for a concession, or go offshore. You can afford one of them.</p>`],
  1973: [`<div class="fd-dateline">16 October 1973 · Houston, Texas</div>`, `<h2 class="fd-intro-head">OPEC has just raised the posted price seventy percent.</h2>`,
    `<p>Tomorrow the Arab producers will cut supply. Washington controls the price of old oil; new oil is free. In the North Sea, BP's Forties field waits for its pipeline.</p>`,
    `<p><b>${esc(opts.founderName)}</b>'s <b>${esc(opts.companyName)}</b> runs Permian waterfloods, a Gulf platform and a jackup.</p>`,
    `<p class="fd-kicker">Drill new oil. Mind the hurricanes. And don't believe the price will stay up forever.</p>`],
  // M4-C: Era V Quick Starts
  2000: [`<div class="fd-dateline">3 January 2000 · Houston, Texas</div>`, `<h2 class="fd-intro-head">Y2K passed quietly. Crude is near $25.</h2>`,
    `<p>Up near Fort Worth, Mitchell Energy keeps fracking the Barnett Shale with plain water and sand — and the wells are getting better. In the Gulf, drillships are reaching for the deep water under the salt.</p>`,
    `<p><b>${esc(opts.founderName)}</b>'s <b>${esc(opts.companyName)}</b> runs tired Permian waterfloods, a shelf platform and two land rigs.</p>`,
    `<p class="fd-kicker">Bet on the shale or buy into the deep water. You can afford one of them.</p>`],
  2014: [`<div class="fd-dateline">20 June 2014 · Midland, Texas</div>`, `<h2 class="fd-intro-head">Brent is $112. Every frac crew in Texas is booked.</h2>`,
    `<p>Rigs are scarce, pipelines are full and the banks lend against every barrel you have proved. In November, in Vienna, OPEC will decide not to cut.</p>`,
    `<p><b>${esc(opts.founderName)}</b>'s <b>${esc(opts.companyName)}</b> has thirty-two horizontals, four DUCs, half its oil hedged at $95 and a reserve-based loan.</p>`,
    `<p class="fd-kicker">Keep the treadmill turning — and be ready when the price breaks.</p>`],
});

export function showIntro(host, opts, { reducedMotion = false } = {}) {
  return new Promise((resolve) => {
    const d = difficultyById(opts.difficulty);
    const lines = [1946, 1973, 2000, 2014].includes(opts.startYear) ? INTRO_M3(opts, esc)[opts.startYear] : // M4-C: + 2000 / 2014
      opts.startYear === 1901 ? [
      `<div class="fd-dateline">January 1901 · Beaumont, Texas</div>`,
      `<h2 class="fd-intro-head">Forty years of Oil Creek are behind you.</h2>`,
      `<p>Down on the Gulf Coast, a one-armed believer named Pattillo Higgins swears there is oil under a salt mound called Spindletop. Captain Lucas and the Hamill brothers are drilling it now with a new kind of rig — one that <i>turns</i> the pipe and pumps mud down the hole.</p>`,
      `<p><b>${esc(opts.founderName)}</b>'s <b>${esc(opts.companyName)}</b> has two wells pumping on Oil Creek, a cable-tool outfit, and one of those new rotary rigs on a flatcar headed south.</p>`,
      `<p class="fd-kicker">Lease a lot on the hill before the world hears about it. Mind your mud weight. And keep the BOP handy — the gas down there doesn't wait for you.</p>`,
    ] : [
      `<div class="fd-dateline">August 1859 · Titusville, Pennsylvania</div>`,
      `<h2 class="fd-intro-head">Colonel Drake has struck oil at 69½ feet.</h2>`,
      `<p>Word runs down Oil Creek faster than the water. Farmers turn wildcatter overnight, whiskey barrels become oil barrels, and a lease that was cow pasture on Sunday sells for a fortune by Friday.</p>`,
      `<p><b>${esc(opts.founderName)}</b> has pooled <b>${esc(money(d.cash))}</b> into <b>${esc(opts.companyName)}</b>: one spring-pole rig, two hired hands, and a nose for oil seeps.</p>`,
      `<p class="fd-kicker">Find a seep. Make hole. And watch the price of crude — it won’t stay at twenty dollars a barrel for long.</p>`,
    ];
    host.innerHTML = `<section class="fd-intro" role="dialog" aria-modal="true" aria-label="Prologue" tabindex="-1">
        <div class="fd-intro-emblem">${emblemSVG(opts.logo, { size: 64, title: opts.companyName })}</div>
        ${lines.map((l, i) => `<div class="fd-intro-line" style="--i:${i}">${l}</div>`).join('')}
        <div class="fd-intro-line fd-intro-go" style="--i:${lines.length}">
          <button type="button" class="fd-btn primary lg" data-act="go">Spud in →</button>
          <span class="fd-hint">Enter to begin · Esc to skip</span>
        </div>
      </section>`;
    const sec = host.querySelector('.fd-intro');
    const go = sec.querySelector('[data-act=go]');
    const revealAll = () => sec.classList.add('shown');
    if (reducedMotion) revealAll();
    let done = false;
    const finish = () => { if (done) return; done = true; document.removeEventListener('keydown', onKey, true); resolve(); };
    // Document-level (capture) so it works wherever focus ended up, and keeps keys from the game's shortcuts.
    const onKey = (e) => {
      if (!sec.isConnected) return document.removeEventListener('keydown', onKey, true);
      e.stopPropagation();
      if (e.key === 'Tab') return;
      if (e.key === 'Escape') { e.preventDefault(); finish(); }
      else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!sec.classList.contains('shown') && !sec.classList.contains('settled')) revealAll(); else finish();
      }
    };
    document.addEventListener('keydown', onKey, true);
    sec.addEventListener('click', (e) => {
      if (e.target.closest('[data-act=go]')) return finish();
      revealAll();
    });
    // When the last line has animated in, Enter goes straight in.
    const last = sec.querySelector('.fd-intro-go');
    last.addEventListener('animationend', () => sec.classList.add('settled'), { once: true });
    requestAnimationFrame(() => { sec.focus(); setTimeout(() => go.focus({ preventScroll: true }), reducedMotion ? 0 : 400); });
  });
}
